/**
 * HomepageCanvases.jsx
 * -----------------------------------------------------------------
 * Canvas-based effects for the homepage below the scroll story:
 *   - <PulseStripCanvas />  — the "market pulse" strip between the
 *     console and the module cards.
 *   - <ModuleCanvas />      — the small dot visual inside each
 *     module card (8 variants). Boot-up choreographed by the parent
 *     via `bootStartAt`; hover-driven live animation otherwise.
 *   - <FinaleCanvas />      — background dot field + ring pulses
 *     spawned from a "target" point (Enter Terminal button).
 *   - <TypedTerminal />     — types a short line one character at
 *     a time when triggered, then a blinking block cursor.
 *
 * Rules baked in throughout:
 *   - Nothing paints on page load. Every canvas mounts an
 *     IntersectionObserver and starts its rAF only while on screen.
 *   - `document.visibilitychange` also pauses everything when the
 *     tab is hidden.
 *   - `prefers-reduced-motion` renders a still one-frame version.
 *   - Small-viewport branch reduces dot counts by ~50%.
 */
import { useEffect, useRef, useState, useCallback } from "react";

// ---------- shared helpers -----------------------------------------

const isReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isSmallViewport = () =>
  typeof window !== "undefined" && window.innerWidth < 768;

// Simple hook: is `ref` currently intersecting? Combined with tab
// visibility so we don't burn CPU when the user tabs away.
function useIsVisible(ref, threshold = 0) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    let intersecting = false;
    const update = () => {
      const tabVis = typeof document !== "undefined" ? !document.hidden : true;
      setVisible(intersecting && tabVis);
    };
    const io = new IntersectionObserver(
      (entries) => {
        intersecting = entries[0]?.isIntersecting ?? false;
        update();
      },
      { threshold }
    );
    io.observe(el);
    const onVis = () => update();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVis);
    }
    return () => {
      io.disconnect();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVis);
      }
    };
  }, [ref, threshold]);
  return visible;
}

// Attach a canvas to devicePixelRatio and keep it sized to its
// parent. Returns [ctx, sizeRef]. Callers should re-read the
// current size from `sizeRef.current` inside their draw.
function useCanvasSizing(canvasRef, wrapRef) {
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const rect = wrap.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      sizeRef.current = { w, h, dpr };
    };
    resize();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      // Wrap in rAF to sidestep the "ResizeObserver loop completed
      // with undelivered notifications" error that fires whenever a
      // resize callback itself mutates layout (which resizing the
      // canvas element does). rAF defers our resize to the next
      // paint tick so the observer never re-fires synchronously.
      ro = new ResizeObserver(() => requestAnimationFrame(resize));
      ro.observe(wrap);
    } else {
      window.addEventListener("resize", resize);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", resize);
    };
  }, [canvasRef, wrapRef]);
  return sizeRef;
}

// -----------------------------------------------------------------
// PulseStripCanvas — 120px full-width heartbeat with drifting labels
// -----------------------------------------------------------------
const PULSE_LABELS = [
  "+14 promo videos detected",
  "5 new creators found",
  "+9 promo videos detected",
  "3 creators back active",
  "+21 promo videos detected",
];

export function PulseStripCanvas() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const size = useCanvasSizing(canvasRef, wrapRef);
  const visible = useIsVisible(wrapRef);
  const stateRef = useRef({
    offset: 0,
    frame: 0,
    nextSpikeIn: 90,
    spikes: [], // {x, ampFrames, life, amp, label, labelIdx}
    labelCursor: 0,
    reduce: isReducedMotion(),
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;

    const draw = () => {
      const { w, h } = size.current;
      const s = stateRef.current;
      ctx.clearRect(0, 0, w, h);

      // ---- top & bottom hairlines
      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, 0.5);
      ctx.lineTo(w, 0.5);
      ctx.moveTo(0, h - 0.5);
      ctx.lineTo(w, h - 0.5);
      ctx.stroke();

      const midY = h / 2;
      // spike bookkeeping: advance/expire
      for (let i = s.spikes.length - 1; i >= 0; i--) {
        const sp = s.spikes[i];
        sp.x -= 1; // drift left with the wave (canvas offset ~1px/frame)
        sp.life += 1;
        if (sp.x < -60) s.spikes.splice(i, 1);
      }
      // spawn new spike?
      if (--s.nextSpikeIn <= 0) {
        const amp = 22 + Math.random() * 14;
        const labelIdx = s.labelCursor % PULSE_LABELS.length;
        s.labelCursor += 1;
        s.spikes.push({
          x: w - 6,
          startFrame: s.frame,
          amp,
          label: PULSE_LABELS[labelIdx],
        });
        s.nextSpikeIn = 80 + Math.floor(Math.random() * 120);
      }

      // ---- heartbeat dot line: dots every ~5px on mobile, 3px otherwise
      const small = isSmallViewport();
      const step = small ? 5 : 3;
      ctx.fillStyle = "rgba(0,240,255,0.75)";
      for (let x = 0; x < w; x += step) {
        // baseline noise
        const gx = (x + s.offset) * 0.06;
        let y = midY + Math.sin(gx) * 1.2 + Math.sin(gx * 3.1) * 0.6;
        // spike contribution — a sharp up-down within ~24px of spike.x
        for (const sp of s.spikes) {
          const d = x - sp.x;
          if (Math.abs(d) < 26) {
            const t = d / 26; // -1..1
            // Two-lobe pulse: up then down
            const shape =
              t < 0
                ? -Math.cos((t + 1) * Math.PI) // rises to +1 at t=0
                : Math.cos(t * Math.PI); //  falls from +1 to -1 at t=1
            y -= shape * sp.amp * 0.9;
          }
        }
        ctx.beginPath();
        ctx.arc(x, y, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }

      // ---- spike labels — drift with the wave, fade to 0 by ~40% across
      ctx.font = '11px ui-monospace, "SF Mono", Menlo, Consolas, monospace';
      ctx.textBaseline = "alphabetic";
      for (const sp of s.spikes) {
        const life = s.frame - sp.startFrame;
        const alpha = Math.max(0, Math.min(1, 1 - (w - sp.x) / (w * 0.6)));
        if (alpha <= 0) continue;
        ctx.fillStyle = `rgba(57,255,20,${alpha * 0.9})`;
        // small vertical tick pointing into the spike
        ctx.strokeStyle = `rgba(57,255,20,${alpha * 0.5})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sp.x, midY - sp.amp - 4);
        ctx.lineTo(sp.x, midY - sp.amp - 12);
        ctx.stroke();
        ctx.fillText(sp.label, sp.x + 6, midY - sp.amp - 6);
        // hint to satisfy no-unused warning:
        void life;
      }

      // ---- head dot at right + pulsing ring
      const headPulse = 1 + Math.sin(s.frame * 0.08) * 0.35;
      ctx.beginPath();
      ctx.arc(w - 8, midY, 3.2, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(w - 8, midY, 6.5 * headPulse, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(0,240,255,${0.6 / headPulse})`;
      ctx.lineWidth = 1;
      ctx.stroke();

      s.offset = (s.offset + 1) % 1000;
      s.frame += 1;
      // Frame skip on mobile to keep TBT under Lighthouse's 4x-CPU budget.
      if (isSmallViewport() && s.frame % 2 === 1) {
        raf = requestAnimationFrame(() => { s.frame += 1; raf = requestAnimationFrame(draw); });
      } else {
        raf = requestAnimationFrame(draw);
      }
    };

    if (stateRef.current.reduce) {
      // Still, one-frame version.
      draw();
      return;
    }
    if (!visible) return;
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [visible, size]);

  return (
    <div
      ref={wrapRef}
      data-testid="market-pulse-strip"
      aria-hidden="true"
      className="relative z-10 w-full"
      style={{ height: 120, overflow: "hidden" }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: "block", position: "absolute", inset: 0 }}
      />
    </div>
  );
}

// -----------------------------------------------------------------
// ModuleCanvas — per-card dot visual
// -----------------------------------------------------------------
// Boot state per card is derived from `bootStartAt` (a shared
// timestamp set by the parent when the card grid enters view).
// Each card computes its own delayed run window: [t0, t0 + 1300].
// After that, the visual freezes on a "rest" frame until hovered.
export function ModuleCanvas({ kind, cardIdx, bootStartAt, hovered, tall = false, wide = false }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const size = useCanvasSizing(canvasRef, wrapRef);
  const visible = useIsVisible(wrapRef);
  const [reduce] = useState(() => isReducedMotion());
  const stateRef = useRef({ frame: 0, tRel: 0, small: isSmallViewport() });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;
    const bootDelay = 140 * cardIdx; // ms after bootStartAt
    const bootDur = 1300;

    const running = () => {
      if (reduce) return false;
      if (hovered) return true;
      if (!bootStartAt) return false;
      // Skip the boot-up cascade entirely on mobile — main thread
      // is precious and the still rest frames read fine.
      if (stateRef.current.small) return false;
      const t = performance.now() - bootStartAt;
      return t >= bootDelay && t <= bootDelay + bootDur;
    };

    const drawFrame = (rest = false) => {
      const { w, h } = size.current;
      const s = stateRef.current;
      ctx.clearRect(0, 0, w, h);
      // dispatch per-kind
      switch (kind) {
        case "discovery":
          drawDiscovery(ctx, w, h, s.frame, rest, s.small);
          break;
        case "sparkline":
          drawSparkline(ctx, w, h, s.frame, rest, s.small);
          break;
        case "network":
          drawNetwork(ctx, w, h, s.frame, rest, s.small);
          break;
        case "matrix":
          drawMatrix(ctx, w, h, s.frame, rest, s.small);
          break;
        case "bars":
          drawBars(ctx, w, h, s.frame, rest, s.small);
          break;
        case "compare":
          drawCompare(ctx, w, h, s.frame, rest, s.small);
          break;
        case "ping":
          drawPing(ctx, w, h, s.frame, rest, s.small);
          break;
        case "radar":
          drawRadar(ctx, w, h, s.frame, rest, s.small);
          break;
        case "pipeline":
          drawPipeline(ctx, w, h, s.frame, rest, s.small);
          break;
        case "botflag":
          drawBotFlag(ctx, w, h, s.frame, rest, s.small);
          break;
        case "livepulse":
          drawLivePulse(ctx, w, h, s.frame, rest, s.small);
          break;
        case "prism":
          drawPrism(ctx, w, h, s.frame, rest, s.small);
          break;
        default:
          break;
      }
    };

    // Render the initial rest frame so the card isn't blank when
    // it comes into view. Skipped entirely until visible to avoid
    // burning main-thread work at page load.
    if (!visible) return;
    drawFrame(true);

    const loop = () => {
      const s = stateRef.current;
      const alive = running();
      if (alive) {
        s.frame += 1;
        drawFrame(false);
        raf = requestAnimationFrame(loop);
      } else {
        // Freeze on a rest frame and stop the loop until hover / boot
        // window reopens (React re-runs the effect on those changes).
        drawFrame(true);
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [kind, cardIdx, bootStartAt, hovered, visible, size, reduce]);

  return (
    <div
      ref={wrapRef}
      data-testid={`module-canvas-${kind}`}
      className="relative w-full h-full"
      // overflow: hidden + absolute-positioned canvas: the canvas is
      // pulled out of flow, so its bitmap size can NEVER feed back
      // into the wrap's height. Without this, kr-card-vis-tall's
      // `height: 100%; min-height: 220px` on a grid-stretched item
      // formed a circular dependency with the canvas's own
      // `display: block` height → ResizeObserver loop → the Prospect
      // Radar card grew to ~1500px per resize tick.
      style={{ overflow: "hidden" }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: "block", position: "absolute", inset: 0 }}
      />
    </div>
  );
}

// ---- per-kind draw functions ------------------------------------
// Each is a pure function of (ctx, w, h, frame, rest, small). At
// rest it draws a still, "designed" frame; otherwise it animates.
// Cyan rgba(0,240,255,x), Green rgba(57,255,20,x).

function drawDiscovery(ctx, w, h, f, rest, small) {
  // Twinkling dot field with a few green dots.
  const cols = small ? 14 : 22;
  const rows = 4;
  const stepX = w / (cols + 1);
  const stepY = h / (rows + 1);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      const green = idx % 17 === 3;
      // twinkle: phase varies by index; at rest use static seed
      const phase = rest ? 0.65 : 0.5 + 0.5 * Math.sin(f * 0.05 + idx * 0.7);
      ctx.beginPath();
      ctx.arc(stepX * (c + 1), stepY * (r + 1), 1.4, 0, Math.PI * 2);
      if (green) ctx.fillStyle = `rgba(57,255,20,${0.35 + phase * 0.55})`;
      else ctx.fillStyle = `rgba(0,240,255,${0.28 + phase * 0.55})`;
      ctx.fill();
    }
  }
}

function drawSparkline(ctx, w, h, f, rest, small) {
  // Dotted wave line + dotted fill under it.
  const seg = small ? 26 : 44;
  const pts = [];
  for (let i = 0; i <= seg; i++) {
    const t = i / seg;
    const x = 6 + t * (w - 12);
    // gently rising wave, with tiny scroll during animation
    const scroll = rest ? 0 : f * 0.03;
    const y =
      h - 12 - t * (h - 30) + Math.sin(t * 6 + scroll) * 4 + Math.sin(t * 12) * 1.5;
    pts.push({ x, y });
  }
  // fill dots — vertical drops from each point to baseline, thinning
  const baseY = h - 6;
  for (const p of pts) {
    const gap = 3;
    for (let y = p.y + 3; y < baseY; y += gap) {
      const alpha = 0.10 + 0.35 * (1 - (y - p.y) / (baseY - p.y));
      ctx.beginPath();
      ctx.arc(p.x, y, 1, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(0,240,255,${alpha})`;
      ctx.fill();
    }
  }
  // line dots
  ctx.fillStyle = "rgba(0,240,255,0.9)";
  for (const p of pts) {
    ctx.beginPath();
    ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
    ctx.fill();
  }
  // head marker
  const last = pts[pts.length - 1];
  ctx.beginPath();
  ctx.arc(last.x, last.y, 3, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(0,240,255,1)";
  ctx.fill();
  const ring = rest ? 6 : 5 + (Math.sin(f * 0.14) + 1) * 2.5;
  ctx.beginPath();
  ctx.arc(last.x, last.y, ring, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(0,240,255,0.55)";
  ctx.lineWidth = 1;
  ctx.stroke();
}

function drawNetwork(ctx, w, h, f, rest, small) {
  // 4 outer ring hubs + centre hub; dots travel along the edges
  const cx = w / 2;
  const cy = h / 2;
  const rings = [
    { x: cx - w * 0.32, y: cy - h * 0.12 },
    { x: cx + w * 0.32, y: cy - h * 0.14 },
    { x: cx - w * 0.28, y: cy + h * 0.18 },
    { x: cx + w * 0.30, y: cy + h * 0.20 },
  ];
  // edges
  ctx.strokeStyle = "rgba(0,240,255,0.16)";
  ctx.lineWidth = 1;
  for (const r of rings) {
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(r.x, r.y);
    ctx.stroke();
  }
  // dots travelling along edges (rest → static midpoint)
  const dotsPer = small ? 4 : 7;
  for (let i = 0; i < rings.length; i++) {
    const r = rings[i];
    for (let k = 0; k < dotsPer; k++) {
      const phase = rest ? k / dotsPer : ((f * 0.006) + (k / dotsPer) + i * 0.05) % 1;
      const x = cx + (r.x - cx) * phase;
      const y = cy + (r.y - cy) * phase;
      ctx.beginPath();
      ctx.arc(x, y, 1.4, 0, Math.PI * 2);
      ctx.fillStyle = i === 0 ? "rgba(57,255,20,0.85)" : "rgba(0,240,255,0.85)";
      ctx.fill();
    }
  }
  // hubs
  const hubR = 9;
  for (const r of rings) {
    ctx.beginPath();
    ctx.arc(r.x, r.y, hubR, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(0,240,255,0.55)";
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(r.x, r.y, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,240,255,0.95)";
    ctx.fill();
  }
  // centre — green anchor
  ctx.beginPath();
  ctx.arc(cx, cy, 12, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(57,255,20,0.6)";
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, 4, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(57,255,20,0.95)";
  ctx.fill();
}

function drawMatrix(ctx, w, h, f, rest, _small) {
  // 2x2 dot grid; bottom-right quadrant pulses green.
  const cols = 12;
  const rows = 5;
  const stepX = w / (cols + 1);
  const stepY = h / (rows + 1);
  const pulse = rest ? 0.9 : 0.65 + 0.35 * Math.sin(f * 0.09);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const qx = c >= cols / 2 ? 1 : 0;
      const qy = r >= rows / 2 ? 1 : 0;
      const lit = qx === 1 && qy === 1;
      ctx.beginPath();
      ctx.arc(stepX * (c + 1), stepY * (r + 1), lit ? 1.7 : 1.1, 0, Math.PI * 2);
      if (lit) ctx.fillStyle = `rgba(57,255,20,${0.5 + pulse * 0.4})`;
      else ctx.fillStyle = "rgba(255,255,255,0.22)";
      ctx.fill();
    }
  }
  // axes
  ctx.strokeStyle = "rgba(255,255,255,0.14)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(w / 2, 4);
  ctx.lineTo(w / 2, h - 4);
  ctx.moveTo(4, h / 2);
  ctx.lineTo(w - 4, h / 2);
  ctx.stroke();
}

function drawBars(ctx, w, h, f, rest, _small) {
  // 7 rising dot bars against a dashed green target line
  const bars = 7;
  const stepX = (w - 20) / (bars - 1);
  const targetY = h * 0.42;
  // dashed target
  ctx.strokeStyle = "rgba(57,255,20,0.65)";
  ctx.setLineDash([4, 4]);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(6, targetY);
  ctx.lineTo(w - 6, targetY);
  ctx.stroke();
  ctx.setLineDash([]);
  // bars
  const heights = [22, 34, 28, 46, 40, 58, 70]; // final heights
  for (let i = 0; i < bars; i++) {
    const t = rest ? 1 : Math.min(1, (f * 0.03 + i * 0.05) % 1.5);
    const drawn = heights[i] * (0.4 + 0.6 * t);
    const x = 10 + i * stepX;
    const dots = Math.round(drawn / 4);
    for (let k = 0; k < dots; k++) {
      const y = h - 8 - k * 4;
      const overTarget = y < targetY;
      ctx.beginPath();
      ctx.arc(x, y, 1.6, 0, Math.PI * 2);
      ctx.fillStyle = overTarget ? "rgba(57,255,20,0.85)" : "rgba(0,240,255,0.85)";
      ctx.fill();
    }
  }
}

function drawCompare(ctx, w, h, f, rest, _small) {
  // Two dot bars (cyan, green) breathing against each other.
  const cx = w / 2;
  const barW = w * 0.34;
  const gap = 14;
  const leftX = cx - gap / 2 - barW;
  const rightX = cx + gap / 2;
  const baseY = h - 8;
  const topY = 8;
  const cols = 12;
  // breathing values
  const t = rest ? 0.7 : 0.5 + 0.5 * Math.sin(f * 0.06);
  const heightA = (baseY - topY) * (0.55 + t * 0.3);
  const heightB = (baseY - topY) * (0.6 + (1 - t) * 0.3);
  const drawBar = (x, height, rgba) => {
    const rows = Math.floor(height / 4);
    const stepX = barW / (cols + 1);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const y = baseY - r * 4;
        ctx.beginPath();
        ctx.arc(x + stepX * (c + 1), y, 1.4, 0, Math.PI * 2);
        ctx.fillStyle = rgba;
        ctx.fill();
      }
    }
  };
  drawBar(leftX, heightA, "rgba(0,240,255,0.85)");
  drawBar(rightX, heightB, "rgba(57,255,20,0.85)");
}

function drawPing(ctx, w, h, f, rest, _small) {
  // Three expanding rings from a green centre dot.
  const cx = w / 2;
  const cy = h / 2;
  const cycle = 90; // frames per ring
  for (let i = 0; i < 3; i++) {
    const t = rest ? (i + 0.5) / 3 : (((f + i * 30) % cycle) / cycle);
    const r = 4 + t * (Math.min(w, h) / 2 - 6);
    const alpha = 0.9 * (1 - t);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(0,240,255,${alpha})`;
    ctx.lineWidth = 1.2;
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.arc(cx, cy, 4, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(57,255,20,1)";
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, 8, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(57,255,20,0.5)";
  ctx.stroke();
}

// v58 — My Radar: a private pipeline. Four dotted status lanes; creator
// dots step from "new" towards "deal", the closed one lights up green.
function drawPipeline(ctx, w, h, f, rest, small) {
  const lanes = 4;
  const W = Math.min(w, h * 1.4);
  const ox = (w - W) / 2;
  const laneW = W / lanes;
  const rows = small ? 7 : 10;
  const stepY = (h - 24) / rows;
  for (let l = 0; l < lanes; l++) {
    const x0 = ox + laneW * l + laneW / 2;
    for (let r = 0; r < rows; r++) {
      ctx.beginPath();
      ctx.arc(x0, 12 + stepY * (r + 0.5), 1.3, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(0,240,255,${0.22 + 0.06 * l})`;
      ctx.fill();
    }
    ctx.beginPath();
    ctx.arc(x0, h - 6, 2, 0, Math.PI * 2);
    ctx.fillStyle = l === lanes - 1 ? "rgba(57,255,20,0.8)" : "rgba(0,240,255,0.5)";
    ctx.fill();
  }
  const cards = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => ({ lane0: i % 3, row: (i * 3 + 1) % rows, speed: 0.004 + (i % 3) * 0.0015 }));
  for (const [i, c] of cards.entries()) {
    const t = rest ? (c.lane0 + 0.15) : (c.lane0 + f * c.speed) % (lanes - 0.01);
    const lane = Math.floor(t);
    const frac = t - lane;
    const move = frac > 0.8 ? (frac - 0.8) / 0.2 : 0;
    const x = ox + laneW * (lane + 0.5 + move);
    const y = 12 + stepY * (c.row + 0.5);
    const deal = lane >= lanes - 1 || (rest && i === 4);
    const cx = rest && i === 4 ? ox + laneW * (lanes - 0.5) : x;
    ctx.beginPath();
    ctx.arc(cx, y, deal ? 3.6 : 3, 0, Math.PI * 2);
    ctx.fillStyle = deal ? "rgba(57,255,20,0.95)" : "rgba(0,240,255,0.9)";
    ctx.fill();
    if (deal) {
      ctx.beginPath();
      ctx.arc(cx, y, rest ? 7 : 6 + (Math.sin(f * 0.12) + 1) * 2, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(57,255,20,0.45)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }
}

// v63 — Bot Detection: a field of creator dots; a scan line passes and a
// few dots light up as flagged (rose ring), the rest stay quiet.
const BOT_FLAGS = [[0.18, 0.3], [0.41, 0.68], [0.63, 0.22], [0.82, 0.58], [0.3, 0.85]];
function drawBotFlag(ctx, w, h, f, rest, small) {
  const cols = small ? 14 : 24;
  const rows = small ? 7 : 10;
  const gx = w / cols;
  const gy = h / rows;
  const scan = rest ? 0.72 : ((f * 0.004) % 1.2) - 0.1;
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      const x = gx * (c + 0.5);
      const y = gy * (r + 0.5);
      const near = Math.max(0, 1 - Math.abs(x / w - scan) * 12);
      ctx.beginPath();
      ctx.arc(x, y, 1.3 + near * 0.8, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(0,240,255,${0.18 + 0.05 * ((c * 7 + r * 3) % 4) + near * 0.4})`;
      ctx.fill();
    }
  }
  ctx.fillStyle = "rgba(0,240,255,0.10)";
  ctx.fillRect(scan * w - 1, 0, 2, h);
  for (const [i, [fx, fy]] of BOT_FLAGS.entries()) {
    const x = gx * (Math.floor(fx * cols) + 0.5);
    const y = gy * (Math.floor(fy * rows) + 0.5);
    const lit = rest || fx < scan;
    if (!lit) continue;
    const pulse = rest ? 0 : (Math.sin(f * 0.1 + i) + 1) * 1.5;
    ctx.beginPath();
    ctx.arc(x, y, 3.4, 0, Math.PI * 2);
    ctx.fillStyle = i % 2 ? "rgba(251,146,60,0.95)" : "rgba(244,63,94,0.95)";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, y, 7 + pulse, 0, Math.PI * 2);
    ctx.strokeStyle = i % 2 ? "rgba(251,146,60,0.45)" : "rgba(244,63,94,0.45)";
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

// v66 — Live Streaming Intel: an "on air" broadcast signal (radio-wave arcs
// around a live dot) with a dotted crowd meter below that rises and falls
// like viewers joining a stream. No dot grid, so it never echoes Bot Detection.
function drawLivePulse(ctx, w, h, f, rest, small) {
  const cx = w / 2;
  const cy = h * 0.36;
  const unit = Math.min(w, h) * (small ? 0.09 : 0.08);
  const spread = Math.PI / 4;
  for (let k = 0; k < 3; k++) {
    const r = unit * (1.4 + k * 1.1);
    const a = rest ? 0.75 - k * 0.18 : 0.25 + 0.7 * Math.max(0, Math.sin(f * 0.08 - k * 0.9));
    ctx.strokeStyle = `rgba(244,63,94,${a})`;
    ctx.lineWidth = 1.6;
    ctx.lineCap = "round";
    for (const base of [0, Math.PI]) {
      ctx.beginPath();
      ctx.arc(cx, cy, r, base - spread, base + spread);
      ctx.stroke();
    }
  }
  const glow = rest ? 0 : (Math.sin(f * 0.08) + 1) * 2;
  ctx.beginPath();
  ctx.arc(cx, cy, unit * 0.55 + glow, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(244,63,94,0.18)";
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, unit * 0.4, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(244,63,94,0.95)";
  ctx.fill();
  const cols = small ? 16 : 26;
  const gap = (w - 24) / (cols - 1);
  const base = h - 8;
  const maxDots = Math.max(3, Math.floor((h * 0.34) / 5));
  for (let i = 0; i < cols; i++) {
    const wave = rest ? 0.5 + 0.4 * Math.sin(i * 0.7) : 0.5 + 0.45 * Math.sin(i * 0.55 + f * 0.05) * Math.cos(i * 0.21 - f * 0.03);
    const n = Math.max(1, Math.round(maxDots * Math.max(0.1, wave)));
    for (let k = 0; k < n; k++) {
      ctx.beginPath();
      ctx.arc(12 + i * gap, base - k * 5, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = k === n - 1 ? "rgba(57,255,20,0.85)" : `rgba(0,240,255,${0.35 + 0.5 * (k / n)})`;
      ctx.fill();
    }
  }
}


// v77 — Creator 360: one gem in the middle, four avenue rays with 3-bar marks.
const PRISM_RAYS = [[-0.62, -0.5, 3], [0.62, -0.5, 2], [-0.62, 0.5, 3], [0.62, 0.5, 1]];
const PRISM_COLORS = { 3: "57,255,20", 2: "251,191,36", 1: "244,63,94" };
function drawPrism(ctx, w, h, f, rest, small) {
  const cx = w / 2;
  const cy = h / 2;
  const s = Math.min(w, h) * (small ? 0.16 : 0.14);
  const spin = rest ? 0 : Math.sin(f * 0.03) * 0.12;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(spin);
  const pts = [[0, -s], [s * 0.8, -s * 0.25], [s * 0.5, s], [-s * 0.5, s], [-s * 0.8, -s * 0.25]];
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.closePath();
  ctx.fillStyle = "rgba(0,240,255,0.10)";
  ctx.fill();
  ctx.strokeStyle = "rgba(0,240,255,0.9)";
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(-s * 0.8, -s * 0.25);
  ctx.lineTo(s * 0.8, -s * 0.25);
  [[0, -s], [-s * 0.5, s], [0, -s], [s * 0.5, s]].forEach(([x, y], i) => (i % 2 ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.strokeStyle = "rgba(0,240,255,0.45)";
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
  PRISM_RAYS.forEach(([dx, dy, lvl], k) => {
    const ex = cx + dx * w * 0.62;
    const ey = cy + dy * h * 0.62;
    const pulse = rest ? 1 : 0.45 + 0.55 * Math.max(0, Math.sin(f * 0.07 - k * 1.3));
    const grad = ctx.createLinearGradient(cx, cy, ex, ey);
    grad.addColorStop(0, "rgba(0,240,255,0.05)");
    grad.addColorStop(1, `rgba(${PRISM_COLORS[lvl]},${0.55 * pulse})`);
    ctx.strokeStyle = grad;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(cx + dx * s * 1.1, cy + dy * s * 1.1);
    ctx.lineTo(ex, ey);
    ctx.stroke();
    const bw = small ? 3 : 4;
    for (let b = 0; b < 3; b++) {
      const bh = (small ? 6 : 8) * (0.5 + b * 0.35);
      ctx.fillStyle = b < lvl ? `rgba(${PRISM_COLORS[lvl]},${0.5 + 0.5 * pulse})` : "rgba(113,113,122,0.45)";
      ctx.fillRect(ex - 8 + b * (bw + 2), ey + 6 - bh, bw, bh);
    }
  });
}

function drawRadar(ctx, w, h, f, rest, small) {
  // Rings, sweeping wedge, blips that light as the sweep passes
  const cx = w / 2;
  const cy = h / 2 + 4;
  const R = Math.min(w * 0.42, h * 0.42);
  // rings
  ctx.strokeStyle = "rgba(0,240,255,0.22)";
  ctx.setLineDash([2, 4]);
  ctx.lineWidth = 1;
  for (let k = 1; k <= 4; k++) {
    ctx.beginPath();
    ctx.arc(cx, cy, (R * k) / 4, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  // crosshairs
  ctx.strokeStyle = "rgba(0,240,255,0.14)";
  ctx.beginPath();
  ctx.moveTo(cx - R, cy);
  ctx.lineTo(cx + R, cy);
  ctx.moveTo(cx, cy - R);
  ctx.lineTo(cx, cy + R);
  ctx.stroke();
  // sweeping wedge
  const angle = rest ? -Math.PI / 3 : (f * 0.03) % (Math.PI * 2);
  const wedge = Math.PI / 3.2;
  const grd = ctx.createLinearGradient(
    cx,
    cy,
    cx + Math.cos(angle) * R,
    cy + Math.sin(angle) * R
  );
  grd.addColorStop(0, "rgba(0,240,255,0.35)");
  grd.addColorStop(1, "rgba(0,240,255,0)");
  ctx.fillStyle = grd;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.arc(cx, cy, R, angle - wedge, angle);
  ctx.closePath();
  ctx.fill();
  // blips
  const blips = small
    ? [{ a: 0.4, r: 0.6 }, { a: 1.9, r: 0.75 }, { a: 3.2, r: 0.45 }, { a: 4.7, r: 0.85 }]
    : [
        { a: 0.35, r: 0.55, tag: true },
        { a: 1.4, r: 0.72 },
        { a: 2.6, r: 0.42, tag: true },
        { a: 3.8, r: 0.86 },
        { a: 5.0, r: 0.60, tag: true },
        { a: 5.9, r: 0.35 },
      ];
  for (const b of blips) {
    const x = cx + Math.cos(b.a) * R * b.r;
    const y = cy + Math.sin(b.a) * R * b.r;
    // Lit while sweep is near this angle
    const diff = ((b.a - angle) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    const near = diff < wedge ? 1 - diff / wedge : 0;
    ctx.beginPath();
    ctx.arc(x, y, 2 + near * 1.6, 0, Math.PI * 2);
    ctx.fillStyle = b.tag
      ? `rgba(57,255,20,${0.7 + near * 0.3})`
      : `rgba(0,240,255,${0.65 + near * 0.35})`;
    ctx.fill();
  }
  // centre
  ctx.beginPath();
  ctx.arc(cx, cy, 3, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(0,240,255,1)";
  ctx.fill();
}

// -----------------------------------------------------------------
// FinaleCanvas — dot field + rings from a target point
// -----------------------------------------------------------------
export function FinaleCanvas({ targetRef }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const size = useCanvasSizing(canvasRef, wrapRef);
  const visible = useIsVisible(wrapRef);
  const [reduce] = useState(() => isReducedMotion());
  const stateRef = useRef({
    dots: null,
    rings: [],
    frame: 0,
    nextRingIn: 0,
    hovered: false,
    target: { x: 0, y: 0 },
  });

  // Watch button hover
  useEffect(() => {
    const btn = targetRef?.current;
    if (!btn) return;
    const enter = () => (stateRef.current.hovered = true);
    const leave = () => (stateRef.current.hovered = false);
    btn.addEventListener("mouseenter", enter);
    btn.addEventListener("mouseleave", leave);
    return () => {
      btn.removeEventListener("mouseenter", enter);
      btn.removeEventListener("mouseleave", leave);
    };
  }, [targetRef]);

  // Recompute target position on layout changes
  const updateTarget = useCallback(() => {
    const btn = targetRef?.current;
    const wrap = wrapRef.current;
    if (!btn || !wrap) return;
    const b = btn.getBoundingClientRect();
    const w = wrap.getBoundingClientRect();
    stateRef.current.target = {
      x: b.left - w.left + b.width / 2,
      y: b.top - w.top + b.height / 2,
    };
  }, [targetRef]);

  useEffect(() => {
    const onResize = () => updateTarget();
    window.addEventListener("resize", onResize);
    // We intentionally do NOT listen to `scroll` here. The target
    // point is computed as `button.rect - wrap.rect`, both of which
    // scroll together — the relative offset only ever changes on
    // layout events (resize, font load), not on scroll. The old
    // per-scroll listener was firing two synchronous getBCR calls on
    // every scroll frame and was the main homepage scroll-jank cause.
    updateTarget();
    // A couple of delayed reads to catch late layout settles (web
    // font load, first paint, image decode).
    const t1 = setTimeout(updateTarget, 250);
    const t2 = setTimeout(updateTarget, 1200);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [updateTarget]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Build dot field lazily on first useful size — but only once
    // we're visible, so page-load main thread stays clean.
    const buildDots = () => {
      const { w, h } = size.current;
      if (!w || !h) return;
      const total = isSmallViewport() ? 90 : 220;
      const arr = [];
      for (let i = 0; i < total; i++) {
        const green = Math.random() < 0.14;
        arr.push({
          x: Math.random() * w,
          y: Math.random() * h,
          base: 0.14 + Math.random() * 0.18,
          lit: 0,
          green,
        });
      }
      stateRef.current.dots = arr;
    };
    let raf = 0;
    // Defer building dots + running effects until visible.
    if (!visible && !reduce) return;
    buildDots();

    const draw = () => {
      const s = stateRef.current;
      const { w, h } = size.current;
      if (!s.dots) {
        buildDots();
      }
      ctx.clearRect(0, 0, w, h);

      // spawn rings
      s.nextRingIn -= 1;
      if (s.nextRingIn <= 0) {
        s.rings.push({ r: 8 });
        // 1.5s at ~60fps ≈ 90 frames; hover: 0.6s ≈ 36
        s.nextRingIn = s.hovered ? 36 : 90;
      }

      // advance rings + light nearby dots
      for (let i = s.rings.length - 1; i >= 0; i--) {
        const ring = s.rings[i];
        ring.r += 2.6;
        const maxR = Math.hypot(w, h);
        const alpha = Math.max(0, 1 - ring.r / maxR);
        ctx.beginPath();
        ctx.arc(s.target.x, s.target.y, ring.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(0,240,255,${alpha * 0.55})`;
        ctx.lineWidth = 1.2;
        ctx.stroke();
        if (ring.r > maxR + 20) s.rings.splice(i, 1);
      }

      // update dot brightness by proximity to any ring's radius
      for (const d of s.dots || []) {
        const dx = d.x - s.target.x;
        const dy = d.y - s.target.y;
        const dist = Math.hypot(dx, dy);
        let hit = 0;
        for (const ring of s.rings) {
          const gap = Math.abs(dist - ring.r);
          if (gap < 20) {
            hit = Math.max(hit, 1 - gap / 20);
          }
        }
        // decay
        d.lit = Math.max(hit, d.lit * 0.96);
        const alpha = Math.min(1, d.base + d.lit * 0.85);
        ctx.beginPath();
        ctx.arc(d.x, d.y, 1.3, 0, Math.PI * 2);
        ctx.fillStyle = d.green
          ? `rgba(57,255,20,${alpha})`
          : `rgba(0,240,255,${alpha})`;
        ctx.fill();
      }

      s.frame += 1;
      raf = requestAnimationFrame(draw);
    };

    if (reduce) {
      // Still frame — just the dot field, no rings.
      const s = stateRef.current;
      const { w, h } = size.current;
      ctx.clearRect(0, 0, w, h);
      for (const d of s.dots || []) {
        ctx.beginPath();
        ctx.arc(d.x, d.y, 1.3, 0, Math.PI * 2);
        ctx.fillStyle = d.green
          ? `rgba(57,255,20,${d.base})`
          : `rgba(0,240,255,${d.base})`;
        ctx.fill();
      }
      return;
    }
    if (!visible) return;
    // A quick target refresh right before we start.
    updateTarget();
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [visible, size, reduce, updateTarget]);

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      data-testid="finale-canvas"
      className="absolute inset-0 z-0"
      style={{ pointerEvents: "none", overflow: "hidden" }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: "block", position: "absolute", inset: 0 }}
      />
    </div>
  );
}

// -----------------------------------------------------------------
// TypedTerminal — types text once, then a blinking block cursor.
// -----------------------------------------------------------------
export function TypedTerminal({ text, className = "", testid, msPerChar = 55, sectionRef }) {
  const [n, setN] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  const [reduce] = useState(() => isReducedMotion());

  useEffect(() => {
    const el = sectionRef?.current || ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setStarted(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [sectionRef]);

  useEffect(() => {
    if (!started) return;
    if (reduce) {
      setN(text.length);
      return;
    }
    let i = 0;
    const iv = setInterval(() => {
      i += 1;
      setN(i);
      if (i >= text.length) clearInterval(iv);
    }, msPerChar);
    return () => clearInterval(iv);
  }, [started, text, msPerChar, reduce]);

  return (
    <span ref={ref} data-testid={testid} className={className}>
      {text.slice(0, n)}
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          width: "0.55em",
          height: "1em",
          background: "currentColor",
          verticalAlign: "-2px",
          marginLeft: 2,
          animation: reduce ? "none" : "kr-caret-blink 1s steps(1, end) infinite",
        }}
      />
    </span>
  );
}
